import { useCallback, useMemo, useRef } from 'react'
import { mapClassicResultToContentDoc } from '../shared/coveoUtils'
import { fetchAEMContent } from '../api/getData'
import contentURL from '../shared/aemContentUrl'
import { useControllerState } from './useControllerState'

/**
 * Manages all content-search state, derived values, and actions for the
 * Solutions & Services, Brands, Articles, and Solutions tabs.
 *
 * Owns the classic (@coveo/headless) engine's controller subscriptions so that
 * SearchContext does not need to know the controller shapes directly.
 *
 * @param {object} params
 * @param {object} params.contentEngine - The classic headless engine instance itself (not a
 *   controller). Used to fire the initial search via executeFirstSearch(), which respects
 *   state buildUrlManager already restored from the URL (q, firstResult, numberOfResults,
 *   af-* facets) instead of resetting pagination the way SearchBox.submit() does.
 * @param {object} params.contentSearchBox
 * @param {object} params.contentPager
 * @param {object} params.contentResultsPerPage
 * @param {Array} params.contentFacets
 * @param {object} params.contentFacetGenerator
 * @param {object} params.contentBreadcrumbManager
 * @param {object} params.contentSummary
 * @param {object} params.contentResultList
 * @param {object} params.contentDidYouMean
 * @param {string} params.locale - e.g. 'en_US'
 */
export function useContentSearch({
  contentEngine,
  contentSearchBox,
  contentPager,
  contentResultsPerPage,
  contentFacets,
  contentFacetGenerator,
  contentBreadcrumbManager,
  contentSummary,
  contentResultList,
  contentDidYouMean,
  locale,
}) {
  // ─── Controller state subscriptions ─────────────────────────────────────
  const contentResultListState     = useControllerState(contentResultList)
  const contentPagerState          = useControllerState(contentPager)
  const contentResultsPerPageState = useControllerState(contentResultsPerPage)
  const contentSummaryState        = useControllerState(contentSummary)
  const contentDidYouMeanState     = useControllerState(contentDidYouMean)
  const contentBreadcrumbState     = useControllerState(contentBreadcrumbManager)
  const availableContentFacets = useMemo(
    () => (contentFacets && contentFacets.length ? contentFacets : contentFacetGenerator.state.automaticFacets || []),
    [contentFacets, contentFacetGenerator.state.automaticFacets]
  )

  // ─── Derived state ───────────────────────────────────────────────────────

  const solutionsServicesDocs = useMemo(
    () => (contentResultListState.results || []).map(mapClassicResultToContentDoc),
    [contentResultListState.results]
  )

  const solutionsServicesRows = useMemo(
    () => contentResultsPerPageState.numberOfResults || 50,
    [contentResultsPerPageState.numberOfResults]
  )

  const solutionsServicesPageNumber = useMemo(
    () => contentPagerState.currentPage || 1,
    [contentPagerState.currentPage]
  )

  const solutionsServicesStart = useMemo(
    () => (solutionsServicesPageNumber - 1) * solutionsServicesRows,
    [solutionsServicesPageNumber, solutionsServicesRows]
  )

  const solutionsServicesFacets = useMemo(
    () => availableContentFacets,
    [availableContentFacets]
  )

  const solutionsServicesSelectedFilters = useMemo(
    () => [
      ...(contentBreadcrumbState.facetBreadcrumbs || []),
      ...(contentBreadcrumbState.automaticFacetBreadcrumbs || []),
    ],
    [contentBreadcrumbState.automaticFacetBreadcrumbs, contentBreadcrumbState.facetBreadcrumbs]
  )

  /**
   * Assembled snapshot of the Solutions & Services tab state.
   * Passed directly into the context value as contentData.solutionsAndServices.
   */
  const nextContentTab = useMemo(() => {
    const nextOrigParams = {
      q:          contentSummaryState.query || new URLSearchParams(window.location.search).get('q') || '',
    }

    return {
      docs:              solutionsServicesDocs,
      facets:            solutionsServicesFacets,
      breadcrumbs:       solutionsServicesSelectedFilters,
      numFound:          contentSummaryState.total || 0,
      isSearching:       !!contentSummaryState.isLoading,
      originalSpelling:  contentDidYouMeanState.originalQuery  || '',
      correctedSpelling: contentDidYouMeanState.wasCorrectedTo || '',
      pageNumber:        solutionsServicesPageNumber,
      start:             solutionsServicesStart,
      rows:              solutionsServicesRows,
      pages:             contentPagerState.maxPage || 1,
      origParams:        nextOrigParams,
    }
  }, [
    contentDidYouMeanState.originalQuery,
    contentDidYouMeanState.wasCorrectedTo,
    contentPagerState.maxPage,
    contentSummaryState.isLoading,
    contentSummaryState.query,
    contentSummaryState.total,
    solutionsServicesDocs,
    solutionsServicesFacets,
    solutionsServicesPageNumber,
    solutionsServicesRows,
    solutionsServicesSelectedFilters,
    solutionsServicesStart,
  ])

  // ─── Actions ─────────────────────────────────────────────────────────────

  // AEM content fetchers (Brands, Articles, Solutions tabs)
  const searchBrands = async ({ searchText, pageSize, currentPage }) => {
    const searchPaths = contentURL('brands', locale)
    const { data } = await fetchAEMContent({ searchText, pageSize, currentPage, searchPaths })
    return data
  }

  const searchArticles = async ({ searchText, pageSize, currentPage }) => {
    const searchPaths = contentURL('articles', locale)
    const { data } = await fetchAEMContent({ searchText, pageSize, currentPage, searchPaths })
    return data
  }

  const searchSolutions = async ({ searchText, pageSize, currentPage }) => {
    const searchPaths = contentURL('solutions', locale)
    const { data } = await fetchAEMContent({ searchText, pageSize, currentPage, searchPaths })
    return data
  }

  // Guards which path searchSolutionsAndServices takes — mirrors useProductSearch.js's
  // hasExecutedFirstSearch ref for the commerce engine.
  const hasExecutedFirstContentSearch = useRef(false)

  /**
   * Drives the Solutions & Services tab — parses queryString params and synchronises
   * all content engine controllers (text, page size, page, sort, facets).
   */
  const searchSolutionsAndServices = useCallback(async ({ queryString }) => {
    const params    = new URLSearchParams(queryString || window.location.search)
    const queryText = params.get('q') || new URLSearchParams(window.location.search).get('q') || ''

    contentSearchBox.updateText(queryText)

    if (!hasExecutedFirstContentSearch.current) {
      hasExecutedFirstContentSearch.current = true
      // Initial search: executeFirstSearch() dispatches the search directly, using
      // whatever state buildUrlManager already restored from the URL (q, firstResult,
      // numberOfResults, af-* facets). Unlike SearchBox.submit() — which always runs
      // prepareForSearchWithQuery and unconditionally dispatches updatePage(1) — this
      // does not reset pagination, so a reload with ?firstResult=50 in the URL lands
      // on the correct page instead of resetting to page 1. It's also a no-op if a
      // search has already executed, matching the commerce engine's equivalent
      // executeFirstSearch()/executeFirstRequest() pattern in useProductSearch.js.
      contentEngine.executeFirstSearch()
    } else {
      // Subsequent searches (typed query, tab reselect, popstate): submitting through
      // SearchBox is the stable path — a genuinely new search is expected to reset to page 1.
      contentSearchBox.submit()
    }

    return { docs: solutionsServicesDocs, numFound: contentSummary.state.total }
  }, [
    contentEngine,
    contentPager,
    contentResultsPerPage,
    contentSearchBox,
    contentSummary.state.total,
    solutionsServicesDocs,
  ])

  /**
   * Handles content-search parameter changes dispatched by pagination, sort,
   * and page-size controls on the content tabs.
   */
  const contentSearchHandler = useCallback(({ key, value }) => {
    switch (key) {
      case 'rows':
        contentResultsPerPage.set(Number(value))
        break
      case 'start': {
        const perPage = contentResultsPerPage.state.numberOfResults || 50
        const targetPage = Math.floor(Number(value) / perPage) + 1
        contentPager.selectPage(targetPage)
        break
      }
      default:
        break
    }
  }, [contentPager, contentResultsPerPage])

  /** Toggles a single content facet value. */
  const setContentFilter = useCallback(({ group, val }) => {
    if (!group || !val) return
    const targetFacet = availableContentFacets.find(
      (facet) => (facet.state.field || facet.state.facetId) === group
    )
    if (!targetFacet) return
    const value = targetFacet.state.values.find((item) => item.value === val)
    if (!value) return
    targetFacet.toggleSelect(value)
  }, [availableContentFacets])

  /** Clears all active content facets. */
  const clearContentFilters = useCallback(() => {
    contentBreadcrumbManager.deselectAll()
  }, [contentBreadcrumbManager])

  /**
   * Re-runs the Solutions & Services search with an optional new query text,
   * preserving all other URL params.
   */
  const searchDocs = useCallback(async ({ searchText }) => {
    const params = new URLSearchParams(window.location.search)
    if (searchText) params.set('q', searchText)
    if (!params.get('q')) params.set('q', '')
    await searchSolutionsAndServices({ queryString: params.toString() })
  }, [searchSolutionsAndServices])

  return {
    // Derived state
    nextContentTab,
    solutionsServicesDocs,
    // AEM content tab fetchers
    searchBrands,
    searchArticles,
    searchSolutions,
    // Coveo content (Solutions & Services) actions
    searchSolutionsAndServices,
    contentSearchHandler,
    setContentFilter,
    clearContentFilters,
    searchDocs,
  }
}
